import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { serializeFilters } from '@/components/table/types/filterSerialization.ts';

const useTableData = <T>({
  queryKey,
  fetchData,
  enabled = true,
}: {
  queryKey: QueryKey;
  fetchData: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ) => Promise<PagedDto<T>>;
  /** Wyłącza zapytanie, gdy tabela nie jest w ogóle rysowana (lista mobilna). */
  enabled?: boolean;
}) => {
  const filterCriteria = useTableStoreContext((state) => state.filterCriteria);
  const pageRequest = useTableStoreContext((state) => state.pageRequest);
  const searchTerm = useTableStoreContext((state) => state.searchTerm);
  const quickFilters = useTableStoreContext((state) => state.quickFilterState);
  const tableDefinition = useTableStoreContext(
    (state) => state.tableDefinition,
  );

  const isInitialized = tableDefinition?.fields?.length > 0;

  const filterCriteriaDto: FilterCriteriaDto = {
    combinator: filterCriteria.combinator,
    filters: serializeFilters(filterCriteria.filters),
    searchTerm: searchTerm.length >= 2 ? searchTerm : '',
    quickFilters: quickFilters,
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      ...queryKey,
      pageRequest,
      filterCriteria,
      searchTerm,
      quickFilters,
    ],
    queryFn: () => fetchData(pageRequest, filterCriteriaDto),
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (previousData) => previousData,
    enabled: enabled && isInitialized,
  });

  return {
    data,
    isLoading,
  };
};

export default useTableData;
